'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './ThreeStepsSection.module.css';

/* Timing — 3 image stages, 6s each (18s total cycle).
   The progress fill lives inside the active step's track, animating
   0→100% over the stage's window. When it hits 100%, the next step
   becomes active and its own fill starts from 0. */
const STAGE_DURATION_MS = 6000;
const STAGE_COUNT = 3;
const TOTAL_CYCLE_MS = STAGE_COUNT * STAGE_DURATION_MS;

/* 3 image stages, one per step. Each ships its true intrinsic size so
   next/image generates the correct srcset for both desktop and mobile.
   The container fixes the aspect ratio and the images are center-cropped
   via object-fit: cover. */
const getStages = (
  t: (key: string) => string,
): Array<{
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Optional mobile-specific override. When set, the mobile render
   *  uses this asset instead of `src`. Other stages without this field
   *  fall back to `src` and rely on `object-fit: cover` to crop the
   *  sides into the portrait-ish mobile frame. */
  mobileSrc?: string;
  mobileWidth?: number;
  mobileHeight?: number;
}> => [
  {
    /* Step 1 (Plan): hands holding the Photon device next to a phone
       showing the suggested-protocols screen. Mobile ships a dedicated
       squarer recomposition so the device + phone both stay legible
       inside the portrait-ish mobile frame. */
    src: 'web_v2/photon/landing-page/three-steps-plan-v3.png',
    alt: t('photon.threeStepsSection.image.planAlt'),
    width: 2400,
    height: 1643,
    mobileSrc: 'web_v2/photon/landing-page/three-steps-plan-mobile-v3.png',
    mobileWidth: 1800,
    mobileHeight: 1757,
  },
  {
    /* Step 2 (Use): the previous Step 1 image now moved here — Photon
       held 5–15 cm from the face for a session. */
    src: 'web_v2/photon/landing-page/three-steps-place.png',
    alt: t('photon.threeStepsSection.image.useAlt'),
    width: 2274,
    height: 1776,
  },
  {
    /* Step 3 (Track): Photon on the knee with the Ring on the hand and
       an adherence card ("10 min sessions everyday · 5/7 days") laid
       over the bottom. */
    src: 'web_v2/photon/landing-page/three-steps-track-v2.png',
    alt: t('photon.threeStepsSection.image.trackAlt'),
    width: 3884,
    height: 2616,
    /* Mobile: portrait-friendly recomposition. Drives the mobile media
       container's aspect-ratio (2528/2616 ≈ 0.97 — slightly taller than
       square). Steps 1 + 2 reuse `src` and get side-cropped via
       object-fit: cover to fit the same height. */
    mobileSrc: 'web_v2/photon/landing-page/three-steps-track-mobile-v3.png',
    mobileWidth: 2528,
    mobileHeight: 2616,
  },
];

type Step = {
  number: string;
  title: string;
  description: string;
};

const getSteps = (t: (key: string) => string): Step[] => [
  {
    number: '1',
    title: t('photon.threeStepsSection.step.plan'),
    description: t('photon.threeStepsSection.step.planDesc'),
  },
  {
    number: '2',
    title: t('photon.threeStepsSection.step.use'),
    description: t('photon.threeStepsSection.step.useDesc'),
  },
  {
    number: '3',
    title: t('photon.threeStepsSection.step.track'),
    description: t('photon.threeStepsSection.step.trackDesc'),
  },
];

const ThreeStepsSection = () => {
  const t = useTranslations('photon');
  const stages = getStages(t);
  const steps = getSteps(t);
  const [activeStage, setActiveStage] = useState(0);
  /* Two ref arrays so the desktop step rows and the mobile pills can each
     own their own progress-fill element. Only one layout is visible at any
     time (display rules), but both are rendered so refs stay attached and
     the rAF tick can write to whichever is on screen without checking
     viewport. */
  const desktopFillRefs = useRef<Array<HTMLDivElement | null>>([]);
  const mobileFillRefs = useRef<Array<HTMLDivElement | null>>([]);
  const startRef = useRef<number>(0);
  const offsetRef = useRef<number>(0);
  const rafRef = useRef<number>(0);

  const tick = useCallback(() => {
    const elapsed =
      (performance.now() - startRef.current + offsetRef.current) %
      TOTAL_CYCLE_MS;
    const stage = Math.floor(elapsed / STAGE_DURATION_MS);

    /* One CSS variable (`--p`) drives both the vertical fill height
       (desktop track) and the horizontal fill width (mobile pill).
       Same value, two orientations — handled in CSS. */
    for (let i = 0; i < STAGE_COUNT; i++) {
      const startMs = i * STAGE_DURATION_MS;
      const endMs = (i + 1) * STAGE_DURATION_MS;
      let fill: number;
      if (elapsed < startMs || elapsed >= endMs) fill = 0;
      else fill = ((elapsed - startMs) / STAGE_DURATION_MS) * 100;
      const dEl = desktopFillRefs.current[i];
      const mEl = mobileFillRefs.current[i];
      if (dEl) dEl.style.setProperty('--p', `${fill}%`);
      if (mEl) mEl.style.setProperty('--p', `${fill}%`);
    }

    setActiveStage((prev) => (prev === stage ? prev : stage));
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) {
      const dEl = desktopFillRefs.current[0];
      const mEl = mobileFillRefs.current[0];
      if (dEl) dEl.style.setProperty('--p', '100%');
      if (mEl) mEl.style.setProperty('--p', '100%');
      return;
    }

    startRef.current = performance.now();
    offsetRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tick]);

  const jumpToStage = useCallback(
    (index: number) => {
      cancelAnimationFrame(rafRef.current);
      startRef.current = performance.now();
      offsetRef.current = index * STAGE_DURATION_MS;

      for (let i = 0; i < STAGE_COUNT; i++) {
        const dEl = desktopFillRefs.current[i];
        const mEl = mobileFillRefs.current[i];
        if (dEl) dEl.style.setProperty('--p', '0%');
        if (mEl) mEl.style.setProperty('--p', '0%');
      }
      setActiveStage(index);
      rafRef.current = requestAnimationFrame(tick);
    },
    [tick],
  );

  const activeStep = steps[activeStage] ?? steps[0];

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.heading}>
          {t('photon.threeStepsSection.heading.howItWorks')}
        </h2>

        {/* ── Desktop: visible ≥769px ── */}
        <div
          className={styles.stepsColumn}
          role="tablist"
          aria-label={t('photon.threeStepsSection.ariaLabel.sessionSteps')}
        >
          {steps.map((step, i) => {
            const isActive = i === activeStage;
            return (
              <button
                className={styles.stepRow}
                key={step.number}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => jumpToStage(i)}
              >
                <div className={styles.progressTrack}>
                  <div
                    className={styles.progressFill}
                    ref={(el) => {
                      desktopFillRefs.current[i] = el;
                    }}
                    aria-hidden="true"
                  />
                </div>
                <div className={styles.stepContent}>
                  <span className={styles.stepTitle}>
                    {step.number} - {step.title}
                  </span>
                  <span
                    className={styles.stepDescription}
                    data-active={isActive}
                  >
                    {step.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className={styles.desktopMedia}>
          {stages.map((stage, i) => (
            <div
              className={styles.imageLayer}
              key={stage.src}
              data-active={i === activeStage}
            >
              <CustomImage
                className={styles.stepImage}
                src={stage.src}
                alt={stage.alt}
                width={stage.width}
                height={stage.height}
                sizes="(max-width: 1320px) 50vw, 600px"
                priority={i === 0}
              />
            </div>
          ))}
        </div>

        {/* ── Mobile: pill row + image + single description — visible ≤768px ── */}
        <div
          className={styles.mobilePillRow}
          role="tablist"
          aria-label={t('photon.threeStepsSection.ariaLabel.sessionSteps')}
        >
          {steps.map((step, i) => {
            const isActive = i === activeStage;
            return (
              <button
                className={styles.mobilePill}
                key={step.number}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-active={isActive}
                onClick={() => jumpToStage(i)}
              >
                <span className={styles.mobilePillLabel}>
                  {step.number} - {step.title}
                </span>
                <div
                  className={styles.mobilePillFill}
                  ref={(el) => {
                    mobileFillRefs.current[i] = el;
                  }}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        <div className={styles.mobileMedia}>
          {stages.map((stage, i) => {
            const src = stage.mobileSrc ?? stage.src;
            const width = stage.mobileWidth ?? stage.width;
            const height = stage.mobileHeight ?? stage.height;
            return (
              <div
                className={styles.imageLayer}
                key={src}
                data-active={i === activeStage}
              >
                <CustomImage
                  className={styles.stepImage}
                  src={src}
                  alt={stage.alt}
                  width={width}
                  height={height}
                  sizes="100vw"
                  priority={i === 0}
                />
              </div>
            );
          })}
        </div>

        <p className={styles.mobileDescription} aria-live="polite">
          {activeStep.description}
        </p>
      </div>
    </section>
  );
};

export default ThreeStepsSection;
